import { useEffect, useState, useRef } from "react";
import { MapContainer, TileLayer, CircleMarker, Tooltip, Popup, GeoJSON, Polyline } from "react-leaflet";
import L from "leaflet";
import { api } from "@/lib/api";
import { useTenant } from "@/contexts/TenantContext";
import { useScenario } from "@/contexts/ScenarioContext";
import { useI18n } from "@/contexts/I18nContext";
import "leaflet/dist/leaflet.css";

delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png",
  iconUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png",
  shadowUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png",
});

// Same color palette as the Dashboard GeoMap so both views stay coherent.
const STATUS = {
  healthy: { color: "#10B981", label: "OK" },
  degraded: { color: "#F59E0B", label: "DEG" },
  critical: { color: "#EF4444", label: "CRIT" },
};

// Official PE municipalities GeoJSON (IBGE) — same source as the Dashboard.
const PE_GEOJSON_URL = "https://raw.githubusercontent.com/tbrugz/geodata-br/master/geojson/geojs-26-mun.json";
const GEOJSON_CACHE_KEY = "evos_pe_geojson_v1";
const GEOJSON_CACHE_TTL_MS = 7 * 24 * 60 * 60 * 1000;

// Highlighted municipalities = the 11 GREs + HQ city (Recife).
const HIGHLIGHT_MUNICIPALITIES = new Set([
  "recife",
  "caruaru",
  "limoeiro",
  "toritama",
  "garanhuns",
  "arcoverde",
  "serra talhada",
  "salgueiro",
  "cabrobo",
  "araripina",
  "belem do sao francisco",
]);

function normalize(s) {
  return (s || "")
    .toLowerCase()
    .normalize("NFD").replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z\s]/g, "")
    .trim();
}

// CircleMarker wrapper that forwards data-testid to the underlying SVG path.
function TaggedCircleMarker({ testId, children, ...props }) {
  const ref = useRef(null);
  useEffect(() => {
    if (ref.current && ref.current._path && testId) {
      ref.current._path.setAttribute("data-testid", testId);
    }
  });
  return <CircleMarker ref={ref} {...props}>{children}</CircleMarker>;
}

export default function TopologyMap() {
  const { tenant } = useTenant();
  const { scenario } = useScenario();
  const { locale } = useI18n();
  const [sites, setSites] = useState([]);
  const [links, setLinks] = useState([]);
  const [geo, setGeo] = useState(null);

  useEffect(() => {
    if (!tenant) return;
    const params = `tenant=${tenant.id}&scenario=${scenario?.id || "live"}`;
    api.get(`/network/sites?${params}`).then(({ data }) => setSites(data));
    api.get(`/network/links?${params}`).then(({ data }) => setLinks(data));
  }, [tenant, scenario]);

  // Load PE GeoJSON with localStorage cache (shared key with Dashboard GeoMap).
  useEffect(() => {
    let cancelled = false;
    try {
      const cached = localStorage.getItem(GEOJSON_CACHE_KEY);
      if (cached) {
        const parsed = JSON.parse(cached);
        if (parsed && parsed.ts && Date.now() - parsed.ts < GEOJSON_CACHE_TTL_MS && parsed.data) {
          setGeo(parsed.data);
          return;
        }
      }
    } catch (_) { /* localStorage unavailable */ }

    fetch(PE_GEOJSON_URL)
      .then((r) => r.json())
      .then((data) => {
        if (cancelled) return;
        setGeo(data);
        try { localStorage.setItem(GEOJSON_CACHE_KEY, JSON.stringify({ ts: Date.now(), data })); }
        catch (_) { /* quota exceeded */ }
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);

  const siteMap = Object.fromEntries(sites.map((s) => [s.id, s]));
  const hub = sites.find((s) => s.type === "hub");

  const styleMunicipality = (feature) => {
    const name = normalize(feature?.properties?.name);
    const isHighlight = HIGHLIGHT_MUNICIPALITIES.has(name);
    return {
      color: isHighlight ? "#3B82F6" : "#334155",
      weight: isHighlight ? 1.4 : 0.5,
      opacity: isHighlight ? 0.95 : 0.55,
      fillColor: isHighlight ? "#1E3A8A" : "#1E293B",
      fillOpacity: isHighlight ? 0.35 : 0.12,
    };
  };

  // PE state center (roughly).
  const center = [-8.3, -37.5];

  return (
    <div className="relative" data-testid="topology-map">
      <MapContainer
        center={center}
        zoom={7}
        scrollWheelZoom={false}
        style={{ height: 460, width: "100%", background: "#020617" }}
        attributionControl={false}
      >
        <TileLayer
          url="https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png"
          subdomains="abcd"
          maxZoom={19}
        />

        {/* PE municipalities (highlighted = SEDU-PE coverage) */}
        {geo && <GeoJSON key="pe-topology-municipalities" data={geo} style={styleMunicipality} />}

        {/* SD-WAN tunnels: hub → each spoke */}
        {hub && links.map((l) => {
          const to = siteMap[l.to];
          if (!to) return null;
          const color = STATUS[l.status]?.color || "#3B82F6";
          const isWireless = l.carrier === "4G/LTE Backup";
          return (
            <Polyline
              key={l.id}
              positions={[[hub.lat, hub.lng], [to.lat, to.lng]]}
              pathOptions={{
                color,
                weight: l.status === "critical" ? 2.5 : 1.6,
                opacity: 0.65,
                dashArray: isWireless ? "6 4" : null,
              }}
            >
              <Tooltip sticky direction="top" opacity={0.95}>
                <div style={{ fontFamily: "IBM Plex Mono", fontSize: 11 }}>
                  <strong>HQ ➜ {to.name}</strong><br />
                  {l.carrier} · {Math.round((l.utilization || 0) * 100)}% util · {STATUS[l.status]?.label}
                </div>
              </Tooltip>
            </Polyline>
          );
        })}

        {/* Site markers (HQ + 11 GREs) */}
        {sites.map((s) => {
          const c = STATUS[s.status]?.color || "#3B82F6";
          const r = s.type === "hub" ? 12 : 9;
          return (
            <TaggedCircleMarker
              key={s.id}
              testId={`site-node-${s.id}`}
              center={[s.lat, s.lng]}
              radius={r}
              pathOptions={{ color: "#020617", weight: 2, fillColor: c, fillOpacity: 0.95 }}
            >
              <Tooltip direction="top" offset={[0, -8]} opacity={0.95}>
                <div style={{ fontFamily: "IBM Plex Mono", fontSize: 11 }}>
                  <strong>{s.name}</strong><br />
                  {s.municipality} · {STATUS[s.status]?.label}
                </div>
              </Tooltip>
              <Popup minWidth={260}>
                <div style={{ minWidth: 240, color: "#F8FAFC" }}>
                  <div style={{ fontSize: 10, letterSpacing: "0.2em", color: "#94A3B8", textTransform: "uppercase", fontFamily: "IBM Plex Mono", marginBottom: 4 }}>
                    {s.type === "hub" ? "HQ" : "GRE"} · {s.municipality}
                  </div>
                  <div style={{ fontSize: 14, fontWeight: 700, marginBottom: 10 }}>{s.name}</div>
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
                    <Cell label={locale === "pt" ? "Vendor" : "Vendor"} value={s.vendor} />
                    <Cell label={locale === "pt" ? "Dispositivos" : "Devices"} value={s.devices} />
                    <Cell label="Throughput" value={`${s.throughput_mbps} Mbps`} />
                    <Cell label={locale === "pt" ? "Latência" : "Latency"} value={s.latency_ms === 0 ? "—" : `${s.latency_ms} ms`} />
                    <Cell label={locale === "pt" ? "Perda" : "Loss"} value={`${s.loss_pct}%`} />
                    <Cell label="Status" value={STATUS[s.status]?.label} color={c} />
                  </div>
                </div>
              </Popup>
            </TaggedCircleMarker>
          );
        })}
      </MapContainer>

      {/* Legend */}
      <div className="absolute bottom-3 left-3 z-[400] flex flex-wrap gap-2 text-[10px] font-mono">
        {Object.entries(STATUS).map(([k, v]) => (
          <div key={k} className="flex items-center gap-1.5 border border-slate-800 bg-[#0F172A]/85 px-2 py-1 backdrop-blur">
            <span className="w-2 h-2 rounded-full" style={{ background: v.color }} />
            <span className="text-slate-400 uppercase tracking-wider">{v.label}</span>
          </div>
        ))}
        <div className="flex items-center gap-1.5 border border-slate-800 bg-[#0F172A]/85 px-2 py-1 backdrop-blur">
          <span className="w-3 h-0.5 bg-slate-400" />
          <span className="text-slate-400 uppercase tracking-wider">{locale === "pt" ? "FIBRA" : "FIBER"}</span>
        </div>
        <div className="flex items-center gap-1.5 border border-slate-800 bg-[#0F172A]/85 px-2 py-1 backdrop-blur">
          <span className="w-3 h-0.5 bg-slate-400" style={{ borderTop: "1px dashed #94A3B8", background: "transparent" }} />
          <span className="text-slate-400 uppercase tracking-wider">{locale === "pt" ? "4G/LTE" : "4G/LTE"}</span>
        </div>
        <div className="flex items-center gap-1.5 border border-slate-800 bg-[#0F172A]/85 px-2 py-1 backdrop-blur">
          <span className="w-3 h-2 border border-[#3B82F6]" style={{ background: "rgba(30,58,138,0.35)" }} />
          <span className="text-slate-400 uppercase tracking-wider">
            {locale === "pt" ? "MUNICÍPIOS ATENDIDOS" : "SERVED MUNICIPALITIES"}
          </span>
        </div>
      </div>

      {!geo && (
        <div className="absolute top-3 right-3 z-[400] text-[10px] font-mono text-slate-500 border border-slate-800 bg-[#0F172A]/85 px-2 py-1 evos-blink">
          {locale === "pt" ? "Carregando municípios de PE..." : "Loading PE municipalities..."}
        </div>
      )}

      <style>{`
        .leaflet-popup-content-wrapper, .leaflet-popup-tip {
          background: #0F172A !important;
          border: 1px solid #1E293B !important;
          color: #F8FAFC !important;
          border-radius: 2px !important;
        }
        .leaflet-popup-content { margin: 12px 14px !important; }
        .leaflet-container a.leaflet-popup-close-button { color: #94A3B8 !important; }
      `}</style>
    </div>
  );
}

function Cell({ label, value, color }) {
  return (
    <div style={{ border: "1px solid #1E293B", background: "#020617", padding: 8 }}>
      <div style={{ fontSize: 9, color: "#64748B", textTransform: "uppercase", letterSpacing: "0.15em", fontFamily: "IBM Plex Mono", marginBottom: 4 }}>
        {label}
      </div>
      <div style={{ fontFamily: "IBM Plex Mono", fontWeight: 700, color: color || "#F8FAFC", fontSize: 13 }}>{value}</div>
    </div>
  );
}
